import { Pagination, PaginationProps, Select, Table } from "antd";
import { AnyObject } from "antd/es/_util/type";
import { ColumnsType, TableProps } from "antd/es/table";
import { ReactNode } from "react";
import JumpPaginationIcon from "../../assets/icons/JumpPaginationIcon";
import NextIcon from "../../assets/icons/NextIcon";
import NextPaginationIcon from "../../assets/icons/NextPaginationIcon";
import PrevIcon from "../../assets/icons/PrevIcon";
import PrevPaginationIcon from "../../assets/icons/PrevPaginationIcon";
import SelectDownIcon from "../../assets/icons/SelectDownIcon";
import { AddZero } from "../../utils/number";
import { selectSearchOption } from "../../utils/utils";

interface Props<DataListType> extends TableProps<any> {
  loading: boolean;
  total?: number;
  columns: ColumnsType<DataListType>;
  dataList: DataListType[];
  page: number;
  limit?: number;
  isSearch?: boolean;
  isPagination?: boolean;
  bordered?: boolean;
  handlePagination?: (paginationPage: number, paginationLimit: number) => void;
  tableTopChildren?: ReactNode;
  tableClass?: string;
  isLimitRow?: boolean;
  showTotalPage?: boolean;
  handleLimit?: (val: number) => void;
  isTopRow?: boolean;
  showColor?: boolean;
  hideSpace?: boolean;
  hideTableColor?: boolean;
  hideFullLoading?: boolean;
  paginationClassnames?: string;
}

const CustomTable = <DataListType extends AnyObject>({
  loading,
  dataList,
  total = dataList?.length,
  limit = 10,
  page,
  columns,
  isPagination = true,
  showColor = true,
  bordered = true,
  handlePagination,
  tableTopChildren,
  isTopRow = true,
  showTotalPage = false,
  hideTableColor = false,
  hideFullLoading = false,
  tableClass = "",
  isLimitRow,
  handleLimit,
  hideSpace,
  paginationClassnames,
  ...rest
}: Props<DataListType>) => {
  const itemRender: PaginationProps["itemRender"] = (
    _,
    type,
    originalElement
  ) => {
    if (type === "prev") {
      return (
        <span>
          <PrevIcon />
        </span>
      );
    }
    if (type === "next") {
      return (
        <span>
          <NextIcon />
        </span>
      );
    }
    return originalElement;
  };

  const totalPage = Math.ceil(total / limit);

  return (
    <div
      className={`border border-solid  dark:border-others-body border-others-borderLight dark:bg-[#242C38] bg-white rounded ${
        !hideSpace ? "my-3" : ""
      }`}
    >
      {isTopRow ? (
        <div
          className={`flex justify-between gap-5 items-center flex-wrap filter_section  ${
            isLimitRow || tableTopChildren ? "p-4" : ""
          }`}
        >
          {isLimitRow ? (
            <div className="flex items-center gap-2 text-header text-sm font-semibold">
              <span>Total Rows</span>
              <Select
                size="middle"
                showSearch
                filterOption={selectSearchOption}
                value={limit}
                options={[
                  {
                    label: "10",
                    value: 10,
                  },
                  {
                    label: "20",
                    value: 20,
                  },
                  {
                    label: "50",
                    value: 50,
                  },
                  {
                    label: "100",
                    value: 100,
                  },
                  {
                    label: "1000",
                    value: 1000,
                  },
                ]}
                onChange={handleLimit}
                className="min-w-[70px] custom-pagination-row"
                suffixIcon={<SelectDownIcon width={14} height={8} />}
              />
              <span>of {AddZero(total)}</span>
            </div>
          ) : null}

          {tableTopChildren ? (
            <div className="ml-auto ">{tableTopChildren}</div>
          ) : null}
        </div>
      ) : null}

      <Table
        columns={columns}
        dataSource={dataList}
        loading={loading}
        pagination={false}
        className={`custom-table custom-scrollbar ${tableClass} ${
          showColor && dataList?.length ? "color-table" : ""
        }`}
        bordered={bordered}
        {...rest}
      />

      {isPagination && dataList?.length ? (
        <div className="custom-pagination">
          <div className="text-header text-sm font-semibold">
            {showTotalPage ? (
              <>
                Showing {page} of {totalPage} {totalPage > 1 ? "Pages" : "Page"}
              </>
            ) : null}
          </div>

          <Pagination
            onChange={handlePagination}
            current={page}
            // showSizeChanger={false}
            total={total}
            itemRender={itemRender}
            pageSize={limit}
            nextIcon={<NextPaginationIcon />}
            prevIcon={<PrevPaginationIcon />}
            jumpNextIcon={<JumpPaginationIcon />}
            jumpPrevIcon={<JumpPaginationIcon />}
            className={paginationClassnames}
          />
        </div>
      ) : null}
    </div>
  );
};

export default CustomTable;
